<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import Bar from '@/components/common/dashboard/chart/Bar.vue'
import Doughnut from '@/components/common/dashboard/chart/Doughnut.vue'

import { getTotalRevGMList, getTotalItemsRevGMList } from '@/api/dashboard'
import { defaultOptions, FCSTMapper, getThisYear } from '@/js/customChart'

import { useNavigatorStore } from "@/store/navigator"
const navigatorStore = useNavigatorStore()
import { useUserStore } from '@/store/user'
const userStore = useUserStore()

// props
const props = defineProps({
  chartType: String,
  employeeId: String,
  legend: String,
  width: Number,
  height: Number,
  title: String,
  data: Object,
  fiscalYear:String
})

// data
const fetchedData = ref()

const APIS = {
  goods: getGoods,
  stage: getStage,
}

// computed
const getChartType = computed(() => {
  const charts = {
    vertical: Bar,
    horizontal: Bar,
    doughnut: Doughnut,
  }
  return charts[props.chartType]
})

const getOptions = computed(() => {
  if (props.chartType !== 'doughnut') {
    return {
      aspectRatio: navigatorStore.isMobile ? 1.2 : 2,
      ...defaultOptions[props.chartType]
    }
  }
  return defaultOptions[props.chartType]
})

// watch
watch([() => props.employeeId, () => props.legend, ()=>props.fiscalYear], async newValue => {
  const [employeeId, legend, fiscalYear] = newValue
  try {
    await APIS[legend](employeeId,fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

watch([() => props.chartType, ()=>props.legend], async newValue => {
  const [chartType] = newValue
  if(chartType == 'doughnut'){
    fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
  }else{
    fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
  }
})

// lifecycle
onMounted(async () => {
  try {
    await APIS[props.legend](userStore.user.employeeId,props.fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

async function getStage(employeeId,fiscalYear) {
  try {
    const result = await getTotalRevGMList({
      fs_year: fiscalYear,
      employee_id: employeeId
    })
    const labels = ["Commit", "Won", "Close"]
    const { data, rawData, dataPer, centerValue, realData } = FCSTMapper(result, 'GM FCST', labels)
    fetchedData.value = {
      data: {
        labels,
        datasets: [
          {
            data:rawData,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ],
            cutout: '70%'
          }
        ]
      },
      additionalData: {
        rawData,
        dataPer,
        centerValue,
        realData,
        data
      }
    }
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
  } catch (err) {
    console.error(err)
  }
}

async function getGoods(employeeId,fiscalYear) {
  try {
    const result = await getTotalItemsRevGMList({
      fs_year: fiscalYear,
      employee_id: employeeId
    })
    const labels = ["H/W", "S/W", "SVC", "ETC"]
    const { data, rawData, dataPer, centerValue ,realData} = FCSTMapper(result, 'GM FCST', labels)
    fetchedData.value = {
      data: {
        labels,
        datasets: [
          {
            data:rawData,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 100, 86, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ],
            cutout: '70%'
          }
        ]
      },
      additionalData: {
        rawData,
        dataPer,
        centerValue,
        realData,
        data
      }
    }
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
  } catch (err) {
    console.error(err)
  }
}

</script>

<template>
  <component :is='getChartType' :width="width" :height="height" :data="fetchedData?.data"
    :additionalData="fetchedData?.additionalData" :title="title" :options="getOptions" :type="chartType"/>
</template>